import { useCallback, useEffect, useRef } from 'react';

/**
 * Creates a debounced version of a callback function that delays execution
 * until after the specified wait time has elapsed since the last invocation.
 *
 * @template T - The type of the callback function to debounce
 * @param {T} callback - The function to debounce
 * @param {number} delay - The number of milliseconds to delay (default: 300ms)
 * @returns {T} A memoized debounced version of the callback
 *
 */
export const useDebounce = <T extends (...args: never[]) => void>(
  callback: T,
  delay: number = 300
): T => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const debouncedCallback = useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    }) as T,
    [delay]
  );

  return debouncedCallback;
};